import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import {
  CERTIFICADO_APELIDO_MAX,
  updateCertificadoApelido,
  type Certificado,
} from '@/api/client';
import { notifyMutationError } from '@/lib/errors';
import { useNotification } from '@/context/NotificationProvider';

interface CertificateApelidoDialogProps {
  /** Certificado em edição; `null` fecha o diálogo. */
  cert: Certificado | null;
  onClose: () => void;
  onSaved: (numeroSerie: string, apelido: string | null) => void;
}

/** Diálogo pequeno para dar, trocar ou remover (campo vazio) o apelido de um certificado. */
export function CertificateApelidoDialog({ cert, onClose, onSaved }: CertificateApelidoDialogProps) {
  const { showSuccess, showError } = useNotification();
  const [valor, setValor] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (cert) setValor(cert.apelido ?? '');
  }, [cert]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cert) return;
    try {
      setSalvando(true);
      const res = await updateCertificadoApelido(cert.numero_serie, valor.trim());
      onSaved(res.numero_serie, res.apelido);
      showSuccess(res.apelido ? 'Apelido salvo.' : 'Apelido removido.');
      onClose();
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={!!cert} onOpenChange={(open: boolean) => !open && !salvando && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Apelido do certificado</DialogTitle>
            <DialogDescription>
              Um nome seu para reconhecer o certificado de {cert?.nome || 'titular não informado'}.
              Deixe em branco para remover.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="apelido-certificado-edicao">Apelido</Label>
            <Input
              id="apelido-certificado-edicao"
              value={valor}
              maxLength={CERTIFICADO_APELIDO_MAX}
              placeholder="Ex.: A1 do escritório"
              autoFocus
              onChange={(e) => setValor(e.target.value)}
            />
            <p className="text-right text-xs text-muted-foreground">
              {valor.length}/{CERTIFICADO_APELIDO_MAX}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={salvando}>
              Cancelar
            </Button>
            <Button type="submit" disabled={salvando} className="gap-2">
              {salvando && <Spinner className="text-inherit" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
